import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import AdminNavigation from './components/AdminNavigation';
import Footer from './components/Footer';
import PullToRefresh from './components/PullToRefresh';
import IssuedSharesList from './components/IssuedSharesList';
import { listIssuedShares } from './utils/apiContentSharing';
import { logout } from './utils/auth';
import { asArray } from './utils/sharesFields';

function summarize(shares) {
  let active = 0;
  let pending = 0;
  for (const s of shares) {
    const st = (s.status || '').toLowerCase();
    if (st === 'active') active += 1;
    else if (st === 'pending_acceptance' || st === 'pending') pending += 1;
  }
  return { total: shares.length, active, pending };
}

function ShareManagement() {
  const [stats, setStats] = useState({ total: 0, active: 0, pending: 0 });
  const [statsLoading, setStatsLoading] = useState(true);
  const [statsError, setStatsError] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);

  const loadStats = useCallback(async () => {
    setStatsLoading(true);
    setStatsError('');
    try {
      const data = await listIssuedShares();
      setStats(summarize(asArray(data)));
    } catch (err) {
      if (err.response?.status === 401) {
        logout();
        return;
      }
      setStatsError('Could not load summary.');
    } finally {
      setStatsLoading(false);
    }
  }, []);

  useEffect(() => { loadStats(); }, [loadStats, refreshKey]);

  const handleRefresh = useCallback(async () => {
    setRefreshKey((k) => k + 1);
  }, []);

  return (
    <PullToRefresh onRefresh={handleRefresh}>
      <div className="relative flex min-h-screen w-full flex-col bg-background-light dark:bg-background-dark">
        <AdminNavigation />
        <div className="flex-grow container mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="mb-6 flex items-start justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Shares</h1>
              <p className="text-gray-600 dark:text-gray-400">Manage the books and chapters you've shared.</p>
            </div>
            <Link
              to="/dashboard"
              className="px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-primary"
            >
              Back to Dashboard
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
            <StatCard label="Total shares" value={statsLoading ? '...' : stats.total} />
            <StatCard label="Active" value={statsLoading ? '...' : stats.active} />
            <StatCard label="Pending invitations" value={statsLoading ? '...' : stats.pending} />
          </div>

          {statsError && (
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <p className="text-sm text-red-800 dark:text-red-200">{statsError}</p>
            </div>
          )}

          <IssuedSharesList key={refreshKey} />
        </div>
        <Footer desktopOnly />
      </div>
    </PullToRefresh>
  );
}

function StatCard({ label, value }) {
  return (
    <div className="bg-white dark:bg-background-dark/50 border border-gray-200 dark:border-gray-800 rounded-lg p-4">
      <div className="text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400">{label}</div>
      <div className="mt-1 text-2xl font-bold text-gray-900 dark:text-white">{value}</div>
    </div>
  );
}

export default ShareManagement;
